import { Stack, Paper, Title, Text, Switch, Group, Button, TextInput, NumberInput, Container, Divider, Grid, Badge } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconLock, IconSparkles } from '@tabler/icons-react';
import { useState, useEffect } from 'react';

const toInt = (val, fallback) => {
  const n = parseInt(val, 10);
  return Number.isFinite(n) ? n : fallback;
};

const toBool = (val, fallback) => {
  if (val === undefined || val === null || val === '') return fallback;
  if (val === '0' || val === 0 || val === false || val === 'false') return false;
  return Boolean(val);
};

const buildInitialSettings = () => {
  const data = window.revotyPhotoReviewsSettings || {};
  return {
    max_photos: toInt(data.max_photos, 5),
    max_file_size: toInt(data.max_file_size, 5),
    photos_required: toBool(data.photos_required, false),
    show_gallery: toBool(data.show_gallery, true),
    gallery_title: data.gallery_title || 'Customer Photos',
    gallery_max_photos: toInt(data.gallery_max_photos, 12),
    show_lightbox_info: toBool(data.show_lightbox_info, true),
    lightbox_show_author: toBool(data.lightbox_show_author, true),
    lightbox_show_rating: toBool(data.lightbox_show_rating, true),
    lightbox_show_comment: toBool(data.lightbox_show_comment, true),
  };
};

export default function PhotoReviewsSettings() {
  const [settings, setSettings] = useState(buildInitialSettings);

  const [loading, setLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(() => toBool(window.revotyPhotoReviewsSettings?.is_premium, false));

  useEffect(() => {
    setSettings(buildInitialSettings());
  }, []);

  const handleSave = async () => {
    setLoading(true);

    const nonce = window.revotyPhotoReviewsData?.nonce || '';

    if (!nonce) {
      notifications.show({
        title: 'Error',
        message: 'Security nonce not found. Please refresh the page.',
        color: 'red',
      });
      setLoading(false);
      return;
    }

    if (!window.ajaxurl) {
      notifications.show({
        title: 'Error',
        message: 'AJAX URL not found. Please refresh the page.',
        color: 'red',
      });
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('action', 'revoty_save_photo_reviews_settings');
    formData.append('nonce', nonce);
    formData.append('settings', JSON.stringify(settings));

    try {
      const response = await fetch(window.ajaxurl, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        notifications.show({
          title: 'Success',
          message: data.data?.message || 'Settings saved successfully',
          color: 'teal',
        });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        notifications.show({
          title: 'Error',
          message: data.data?.message || 'Failed to save settings',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'An error occurred while saving settings',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size="xl" p={0}>
      <Stack gap="xl">
        {/* Upload Settings */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={3} mb="xs">Upload Settings</Title>
              <Text size="sm" c="dimmed">
                Control how customers can upload photos with their reviews
              </Text>
            </div>

            <Divider />

            <Switch
              size="md"
              label="Make Photos Required"
              description="Force customers to upload at least one photo with their review"
              checked={settings.photos_required}
              onChange={(event) => setSettings({ ...settings, photos_required: event.currentTarget.checked })}
              styles={{
                body: { alignItems: 'flex-start' },
                labelWrapper: { paddingTop: 2 },
              }}
            />

            <Grid gutter="lg">
              <Grid.Col span={{ base: 12, md: 6 }}>
                {isPremium ? (
                  <NumberInput
                    label="Maximum Photos"
                    description="Maximum number of photos each customer can upload"
                    placeholder="5"
                    min={1}
                    max={10}
                    clampBehavior="strict"
                    allowDecimal={false}
                    allowNegative={false}
                    value={settings.max_photos}
                    onChange={(value) => setSettings({ ...settings, max_photos: Number(value) || 1 })}
                    suffix=" photos"
                  />
                ) : (
                  <Stack gap="xs">
                    <NumberInput
                      label={
                        <Group gap="xs">
                          <span>Maximum Photos</span>
                          <Badge size="sm" color="orange" leftSection={<IconLock size={12} />}>Pro</Badge>
                        </Group>
                      }
                      description="Maximum number of photos each customer can upload"
                      placeholder="3"
                      min={3}
                      max={3}
                      value={3}
                      suffix=" photos"
                      disabled
                      readOnly
                      styles={{
                        input: {
                          cursor: 'not-allowed',
                          opacity: 0.6,
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="light"
                      color="orange"
                      leftSection={<IconSparkles size={16} />}
                      component="a"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const url = window.revotyData?.upgradeUrl;
                        if (url) {
                          window.location.href = url;
                        }
                      }}
                    >
                      Upgrade to Pro
                    </Button>
                  </Stack>
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                {isPremium ? (
                  <NumberInput
                    label="Maximum File Size"
                    description="Maximum file size for each photo"
                    placeholder="5"
                    min={1}
                    max={10}
                    clampBehavior="strict"
                    allowDecimal={false}
                    allowNegative={false}
                    value={settings.max_file_size}
                    onChange={(value) => setSettings({ ...settings, max_file_size: Number(value) || 1 })}
                    suffix=" MB"
                  />
                ) : (
                  <Stack gap="xs">
                    <NumberInput
                      label={
                        <Group gap="xs">
                          <span>Maximum File Size</span>
                          <Badge size="sm" color="orange" leftSection={<IconLock size={12} />}>Pro</Badge>
                        </Group>
                      }
                      description="Maximum file size for each photo"
                      placeholder="3"
                      min={3}
                      max={3}
                      value={3}
                      suffix=" MB"
                      disabled
                      readOnly
                      styles={{
                        input: {
                          cursor: 'not-allowed',
                          opacity: 0.6,
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="light"
                      color="orange"
                      leftSection={<IconSparkles size={16} />}
                      component="a"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const url = window.revotyData?.upgradeUrl;
                        if (url) {
                          window.location.href = url;
                        }
                      }}
                    >
                      Upgrade to Pro
                    </Button>
                  </Stack>
                )}
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>

        {/* Gallery Settings */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={3} mb="xs">Gallery Settings</Title>
              <Text size="sm" c="dimmed">
                Configure the customer photos gallery displayed at the top of reviews
              </Text>
            </div>

            <Divider />

            <Switch
              size="md"
              label="Show Photo Gallery"
              description="Display a gallery of all customer photos at the top of the reviews section"
              checked={settings.show_gallery}
              onChange={(event) => setSettings({ ...settings, show_gallery: event.currentTarget.checked })}
              styles={{
                body: { alignItems: 'flex-start' },
                labelWrapper: { paddingTop: 2 },
              }}
            />

            <TextInput
              label="Gallery Title"
              description="Title shown above the photo gallery"
              placeholder="Customer Photos"
              value={settings.gallery_title}
              onChange={(event) => setSettings({ ...settings, gallery_title: event.currentTarget.value })}
              style={{ maxWidth: 400 }}
            />

            <NumberInput
              label="Maximum Photos in Gallery"
              description="Maximum number of photos to display in the gallery (rest will be in reviews below)"
              placeholder="12"
              min={1}
              max={50}
              value={settings.gallery_max_photos}
              onChange={(value) => setSettings({ ...settings, gallery_max_photos: Number(value) || 1 })}
              suffix=" photos"
              style={{ maxWidth: 400 }}
            />
          </Stack>
        </Paper>

        {/* Lightbox Settings */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={3} mb="xs">Lightbox Settings</Title>
              <Text size="sm" c="dimmed">
                Control what information is shown in the photo lightbox
              </Text>
            </div>

            <Divider />

            {isPremium ? (
              <>
                <Switch
                  size="md"
                  label="Show Info Bar"
                  description="Display an information bar at the bottom of lightbox images"
                  checked={settings.show_lightbox_info}
                  onChange={(event) => setSettings({ ...settings, show_lightbox_info: event.currentTarget.checked })}
                  styles={{
                    body: { alignItems: 'flex-start' },
                    labelWrapper: { paddingTop: 2 },
                  }}
                />

                {settings.show_lightbox_info && (
                  <Stack gap="md" ml="lg">
                    <Switch
                      size="md"
                      label="Show Author Name"
                      description="Display reviewer's name in the info bar"
                      checked={settings.lightbox_show_author}
                      onChange={(event) => setSettings({ ...settings, lightbox_show_author: event.currentTarget.checked })}
                      styles={{
                        body: { alignItems: 'flex-start' },
                        labelWrapper: { paddingTop: 2 },
                      }}
                    />

                    <Switch
                      size="md"
                      label="Show Rating Stars"
                      description="Display star rating in the info bar"
                      checked={settings.lightbox_show_rating}
                      onChange={(event) => setSettings({ ...settings, lightbox_show_rating: event.currentTarget.checked })}
                      styles={{
                        body: { alignItems: 'flex-start' },
                        labelWrapper: { paddingTop: 2 },
                      }}
                    />

                    <Switch
                      size="md"
                      label="Show Review Comment"
                      description="Display review text excerpt in the info bar"
                      checked={settings.lightbox_show_comment}
                      onChange={(event) => setSettings({ ...settings, lightbox_show_comment: event.currentTarget.checked })}
                      styles={{
                        body: { alignItems: 'flex-start' },
                        labelWrapper: { paddingTop: 2 },
                      }}
                    />
                  </Stack>
                )}
              </>
            ) : (
              <>
                <Switch
                  size="md"
                  label={
                    <Group gap="xs">
                      <span>Show Info Bar</span>
                      <Badge size="sm" color="orange" leftSection={<IconLock size={12} />}>Pro</Badge>
                    </Group>
                  }
                  description="Display an information bar at the bottom of lightbox images (Pro only)"
                  checked={false}
                  disabled
                  styles={{
                    body: { alignItems: 'flex-start' },
                    labelWrapper: { paddingTop: 2 },
                  }}
                />

                <Stack gap="md" ml="lg" style={{ opacity: 0.5 }}>
                  <Switch
                    size="md"
                    label="Show Author Name"
                    description="Display reviewer's name in the info bar"
                    checked={false}
                    disabled
                    styles={{
                      body: { alignItems: 'flex-start' },
                      labelWrapper: { paddingTop: 2 },
                    }}
                  />

                  <Switch
                    size="md"
                    label="Show Rating Stars"
                    description="Display star rating in the info bar"
                    checked={false}
                    disabled
                    styles={{
                      body: { alignItems: 'flex-start' },
                      labelWrapper: { paddingTop: 2 },
                    }}
                  />

                  <Switch
                    size="md"
                    label="Show Review Comment"
                    description="Display review text excerpt in the info bar"
                    checked={false}
                    disabled
                    styles={{
                      body: { alignItems: 'flex-start' },
                      labelWrapper: { paddingTop: 2 },
                    }}
                  />
                </Stack>
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button
                size="md"
                onClick={handleSave}
                loading={loading}
              >
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
